import { GetItemCounts, useInfiniteReferralsCommissionEarned } from '@ukpostbox/client-data';
import {
	ContentBlock,
	ContentLoader,
	FlexAlign,
	FlexJustify,
	InfiniteLoadTrigger,
	ItemRow,
	ItemRowHeader,
	RichEmptyState,
	StackHorizontal,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FormatCurrency, FormatLocaleDateTime, FormatLocaleDateTimeMonthYear } from '@ukpostbox/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { CommissionImage } from '../../Images/CommissionImage';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';

function CommissionEarned() {
	const { t } = useTranslation();
	const { currentLocale, updatePageMeta } = useSite();

	const {
		data,
		status,
		error,
		dataUpdatedAt,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		isStale,
		isRefetching,
	} = useInfiniteReferralsCommissionEarned();

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const commissionEarnedRef = useRef<any>(null);

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [dataUpdatedAt, data]);

	useEffect(() => {
		updatePageMeta({
			title: t(`settings.sections.${'Commission Earned'}`),
		});
	}, [currentLocale]);

	return (
		<>
			<StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass='mb-xs'>
				<h2 className='mb-0'>{t(`settings.sections.${'Commission Earned'}`)}</h2>
			</StackHorizontal>

			<div className='card commissions-item-rows' ref={commissionEarnedRef}>
				<ItemRowHeader title='Referral List Header' sticky>
					<div>{t('settings.referralCommissionEarned.headings.date')}</div>
					<div>{t('settings.referralCommissionEarned.headings.earned')}</div>
					<div>{t('settings.referralCommissionEarned.headings.split')}</div>
					<div>{t('settings.referralCommissionEarned.headings.splitClientShare')}</div>
					<div>{t('settings.referralCommissionEarned.headings.splitOtherShare')}</div>
					<div>{t('settings.referralCommissionEarned.headings.datePaid')}</div>
				</ItemRowHeader>

				{status === 'pending' ? (
					<ContentLoader childHeight='5em' childCount={10} />
				) : status === 'error' ? (
					<ContentBlock error stacked title={error?.name} content={error?.message} />
				) : (
					<>
						{data.pages.map((group) => {
							if (group.totalRecords === 0) {
								return (
									<RichEmptyState
										title={t('settings.referralCommissionEarned.noData.title')}
										description={t('settings.referralCommissionEarned.noData.description')}
										media={<CommissionImage />}
									/>
								);
							}

							return (
								<>
									{group.data.map((commissionEntry, index) => (
										<ItemRow
											key={`commissionEntry${commissionEntry}`}
											title={`Commission Entry ${index}`}
											static
										>
											<div>
												{commissionEntry.dateEarned
													? FormatLocaleDateTimeMonthYear(
															commissionEntry.dateEarned,
															currentLocale,
														)
													: ''}
											</div>
											<div>{FormatCurrency(commissionEntry.earned)}</div>
											<div>{commissionEntry.split}</div>
											<div>{commissionEntry.splitClientShare}</div>
											<div>{commissionEntry.splitOtherShare}</div>
											<div>
												{commissionEntry.datePaid
													? FormatLocaleDateTime(commissionEntry.datePaid, currentLocale)
													: ''}
											</div>
										</ItemRow>
									))}
								</>
							);
						})}

						{itemCounts.total > 0 && (
							<InfiniteLoadTrigger
								loaded={itemCounts.loaded}
								totalCount={itemCounts.total}
								hasNextPage={hasNextPage}
								fetchNextPage={fetchNextPage}
								isStale={isStale}
								isRefetching={isRefetching}
								isFetchingNextPage={isFetchingNextPage}
							/>
						)}
					</>
				)}
			</div>
		</>
	);
}

export { CommissionEarned };
